import { createFileRoute, notFound } from "@tanstack/react-router";
import { PageShell } from "@/components/site/PageShell";
import { GeoServicePage } from "@/components/site/GeoServicePage";
import { geoBreadcrumbJsonLd, geoServiceJsonLd } from "@/lib/jsonld-servicios";
import { getDistrito } from "@/data/geo";
import { seoMeta, canonicalLink, robotsMeta, defaultRobotsIndex } from "@/lib/seo";
import { applyServiceOverride, type ServiceSchemaData } from "@/lib/seo-schema-apply";

const SERVICE_NAME = "Activaciones BTL";
const SERVICE_SLUG = "/activaciones-btl" as const;

const INCLUDES = [
  "Concepto creativo y plan de ejecución",
  "Producción gráfica, mobiliario y stands",
  "Personal de campo seleccionado y uniformado",
  "Supervisión en sitio y coordinación de proveedores",
  "Reporte final con fotos, cobertura y observaciones",
];

const AUDIENCE = [
  "Marcas que quieren visibilidad en calle o PDV",
  "Empresas con lanzamientos o relanzamientos",
  "Equipos de marketing que necesitan ejecución ordenada",
  "Marcas que buscan probar producto con público real",
];

const BENEFITS = [
  {
    title: "Una sola agencia",
    desc: "Idea, producción y ejecución coordinadas por el mismo equipo.",
  },
  {
    title: "Propuesta clara",
    desc: "Presupuesto desglosado por línea, sin sorpresas en el camino.",
  },
  {
    title: "Operación supervisada",
    desc: "Supervisor en sitio en cada activación, no solo personal.",
  },
];

export const Route = createFileRoute("/activaciones-btl/$distrito")({
  loader: ({ params }) => {
    const distrito = getDistrito(params.distrito);
    if (!distrito) throw notFound();
    return { distrito };
  },
  head: ({ loaderData, match }) => {
    const distrito = loaderData?.distrito;
    if (!distrito) return {};
    const path = `${SERVICE_SLUG}/${distrito.slug}`;
    const override = match.context.seoOverride;
    return {
      meta: [
        ...seoMeta({
          title:
            override?.metaTitle ?? `Activaciones BTL en ${distrito.name}, Lima | Improve Yourself`,
          description:
            override?.metaDescription ??
            `Agencia de activaciones BTL en ${distrito.name}, Lima. Activaciones de marca, calle y PDV con personal propio y supervisión en sitio. Propuesta en 72 h.`,
          path,
          image: override?.ogImage ?? undefined,
        }),
        ...robotsMeta(
          override?.robotsIndex ?? defaultRobotsIndex(path),
          override?.robotsFollow ?? true,
        ),
      ],
      links: canonicalLink(override?.canonicalPath ?? path),
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(
            applyServiceOverride(
              geoServiceJsonLd(SERVICE_NAME, SERVICE_SLUG, distrito),
              override?.schemaType === "service"
                ? (override.schemaData as Partial<ServiceSchemaData>)
                : null,
            ),
          ),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify(geoBreadcrumbJsonLd(SERVICE_NAME, SERVICE_SLUG, distrito)),
        },
      ],
    };
  },
  component: ActivacionesBtlDistrito,
});

function ActivacionesBtlDistrito() {
  const { distrito } = Route.useLoaderData();
  return (
    <PageShell>
      <GeoServicePage
        slug={SERVICE_SLUG}
        serviceName={SERVICE_NAME}
        distrito={distrito}
        includes={INCLUDES}
        audience={AUDIENCE}
        benefits={BENEFITS}
      />
    </PageShell>
  );
}
